import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, FileClock, CheckCircle2, Timer, ListChecks, Loader2, CalendarCheck, ChevronDown } from "lucide-react";
import { usePersistedState } from "@/lib/usePersistedState";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { StatCard } from "@/components/StatCard";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { weekLabel, fmtHrs, groupByMonth } from "@/lib/timesheet";

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sheets, setSheets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [closedMonths, setClosedMonths] = usePersistedState("mytimesheets:closedMonths", []);
  const toggleMonth = (ym) => setClosedMonths((c) => (c.includes(ym) ? c.filter((x) => x !== ym) : [...c, ym]));
  const monthHours = (items) => items.reduce((t, s) => t + (s.summary?.total_worked || 0), 0);
  const groups = useMemo(() => groupByMonth(filter === "all" ? sheets : sheets.filter((s) => s.status === filter)), [sheets, filter]);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([api.get("/timesheets"), api.get("/timesheets/stats")]);
      setSheets(a.data);
      setStats(b.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
            Welcome, {user?.name?.split(" ")[0]}
          </h1>
          <p className="mt-1 text-muted-foreground">Your weekly timesheets.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="month-end-btn" variant="outline" onClick={() => navigate("/month")} className="gap-2">
            <CalendarCheck className="h-4 w-4" /> Month end
          </Button>
          <Button data-testid="new-timesheet-btn" onClick={() => navigate("/timesheet/new")} className="gap-2">
            <Plus className="h-4 w-4" /> New Weekly Timesheet
          </Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testId="tile-hours" onClick={() => setFilter("all")} active={filter === "all"} title="Hours Worked" metric={stats ? fmtHrs(stats.total_hours) : "-"} sub="Standard + overtime" icon={Timer} accent="bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300" />
        <StatCard testId="tile-drafts" onClick={() => setFilter("draft")} active={filter === "draft"} title="Drafts" metric={stats ? stats.draft : "-"} sub="Not yet submitted" icon={FileClock} accent="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" />
        <StatCard testId="tile-awaiting" onClick={() => setFilter("submitted")} active={filter === "submitted"} title="Awaiting Approval" metric={stats ? stats.submitted : "-"} sub="With Mokora" icon={ListChecks} accent="bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300" />
        <StatCard testId="tile-approved" onClick={() => setFilter("approved")} active={filter === "approved"} title="Approved" metric={stats ? stats.approved : "-"} sub="Signed off" icon={CheckCircle2} accent="bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" />
      </div>

      <Card className="mt-6 overflow-hidden" data-testid="employee-dashboard-view">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <h2 className="font-heading text-lg font-bold tracking-tight">My Timesheets</h2>
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList data-testid="employee-filter-tabs">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="draft">Drafts</TabsTrigger>
              <TabsTrigger value="submitted">Awaiting</TabsTrigger>
              <TabsTrigger value="approved">Approved</TabsTrigger>
              <TabsTrigger value="rejected">Rejected</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : groups.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground">{sheets.length === 0 ? "No timesheets yet. Create your first week." : "Nothing here right now."}</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Week</TableHead>
                  <TableHead className="text-right">Worked</TableHead>
                  <TableHead className="text-right">Leave</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Mokora Note</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map((g) => [
                  <TableRow key={`g-${g.ym}`} data-testid={`employee-month-group-${g.ym}`} className="bg-secondary/40 hover:bg-secondary/40">
                    <TableCell colSpan={6} className="py-2">
                      <div className="flex items-center justify-between gap-3">
                        <button type="button" data-testid={`employee-month-toggle-${g.ym}`} onClick={() => toggleMonth(g.ym)} className="flex flex-1 items-center gap-2 text-left" aria-expanded={!closedMonths.includes(g.ym)}>
                          <ChevronDown className={`h-4 w-4 transition-transform ${closedMonths.includes(g.ym) ? "-rotate-90" : ""}`} />
                          <span className="font-heading text-sm font-bold">{g.label}</span>
                          <span className="text-xs text-muted-foreground" data-testid={`employee-month-summary-${g.ym}`}>{g.items.length} week{g.items.length === 1 ? "" : "s"} · {fmtHrs(monthHours(g.items))} hrs</span>
                        </button>
                        <Button data-testid={`employee-month-open-${g.ym}`} variant="ghost" size="sm" className="h-7 gap-1.5 text-xs" onClick={() => navigate(`/month/${g.ym}`)}>
                          <CalendarCheck className="h-3.5 w-3.5" /> Month end
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>,
                  ...(closedMonths.includes(g.ym) ? [] : g.items).map((ts) => (
                  <TableRow
                    key={`${g.ym}-${ts.id}`}
                    data-testid={ts.week_ending.startsWith(g.ym) ? `timesheet-row-${ts.id}` : `timesheet-row-${ts.id}-${g.ym}`}
                    className="cursor-pointer"
                    onClick={() => navigate(`/timesheet/${ts.id}`)}
                  >
                    <TableCell className="font-medium">{weekLabel(ts.week_ending)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold tabular">{fmtHrs(ts.summary?.total_worked || 0)}</TableCell>
                    <TableCell className="text-right font-mono tabular text-muted-foreground">{fmtHrs(ts.summary?.total_leave || 0)}</TableCell>
                    <TableCell><StatusBadge status={ts.status} /></TableCell>
                    <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">{ts.manager_comment || "-"}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/timesheet/${ts.id}`); }}>
                        {ts.status === "draft" || ts.status === "rejected" ? "Edit" : "View"}
                      </Button>
                    </TableCell>
                  </TableRow>
                  )),
                ])}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </Layout>
  );
}
